'use client';

import React, { useCallback } from 'react';
import { Spinner, Chip } from '@heroui/react';
import { Typography, UniversalTable } from '../../../../components';
import { useTranslations } from '../../../../contexts/translation.context';
import { EmptyContentIc } from '../../../../components/icons';
import { CostsTableProps, RevshareCost } from '../types';
import { getTableColumns } from '../constants';
import IdCell from './IdCell';
import CampaignCell from './CampaignCell';
import AmountCell from './AmountCell';
import CommentCell from './CommentCell';
import StatusCell from './StatusCell';
import DateCell from './DateCell';

const CostsTable: React.FC<CostsTableProps> = ({
  costs,
  isLoading,
  totalCount,
  totalAmount,
  currentPage,
  pageSize,
  onPageChange,
  onPageSizeChange,
}) => {
  const { t, lang } = useTranslations();

  const renderCell = useCallback((cost: RevshareCost, columnKey: string) => {
    switch (columnKey) {
      case 'id':
        return <IdCell cost={cost} />;
      case 'campaign':
        return <CampaignCell cost={cost} />;
      case 'amount':
        return <AmountCell cost={cost} />;
      case 'comment':
        return <CommentCell cost={cost} />;
      case 'status':
        return <StatusCell cost={cost} />;
      case 'date':
        return <DateCell cost={cost} />;
      default:
        return null;
    }
  }, []);

  // Custom columns with amount header badge
  const columns = getTableColumns(t).map((col) => {
    if (col.key === 'amount') {
      return {
        ...col,
        name: (
          <div className="flex items-center gap-2">
            <span>{col.name}</span>
            <Chip
              size="sm"
              classNames={{
                base: 'bg-[rgba(212,212,216,0.4)] h-5 px-2',
                content: 'text-auth-muted text-xs font-medium p-0',
              }}
            >
              ${totalAmount.toFixed(2)}
            </Chip>
          </div>
        ),
      };
    }
    return col;
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner
          size="lg"
          classNames={{
            circle1: '!border-b-partner',
            circle2: '!border-b-partner/50',
          }}
        />
      </div>
    );
  }

  return (
    <UniversalTable
      mobileSize="2xl"
      key={lang}
      data={costs}
      columns={columns}
      renderCell={renderCell}
      emptyContent={
        <div className="text-center py-12">
          <div className="flex flex-col items-center gap-4">
            <EmptyContentIc />
            <div className="flex flex-col gap-1.5">
              <Typography variant="h6" className="text-xl font-semibold leading-7">
                {t('partner.revshare.costs.noCosts')}
              </Typography>
              <Typography
                className="text-neutral-500 text-base font-medium leading-6"
                variant="body1"
              >
                {t('partner.revshare.costs.noCostsDescription')}
              </Typography>
            </div>
          </div>
        </div>
      }
      pagination={{
        count: totalCount,
        page_size: pageSize,
        page: currentPage,
      }}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      currentPage={currentPage}
      currentPageSize={pageSize}
    />
  );
};

export default CostsTable;
